import getFiltersQueryString from '#commerce/utils/filters/getFiltersQueryString'
import getUrlSafeFilters from '#commerce/utils/filters/getUrlSafeFilters'
import { getUrlId } from '#commerce/utils/getUrlId'
import { extractBloomReachProduct } from '#core/utils/p13n'
import { useAgentforceChat } from '@vfc/agentforce-chat'

interface WelcomeProduct {
  id: string
  title: string
  image: string
  price: string | number
  currency?: string
  url: string
}

export function useAgentforceHostBindings() {
  const MOBILE_BREAKPOINT = 480

  // ── Capture all Nuxt composables at setup time ──
  const { $locale, $t, $gtm } = useNuxtApp()
  const route = useRoute()
  const router = useRouter()
  const cart = useCartStore()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const agentProductsStore = useAgentProductsStore()
  const {
    close: chatClose,
    postContextMessage: postCtx,
    sendSilentMessage,
    renderProductDetails,
    updateLastProductDetails,
    setWelcomeContext,
    renderSearchResults,
    pushBotMessage,
    restartSession,
    setLoading,
    setPendingContext,
    isReady,
  } = useAgentforceChat()
  const { consumer: consumerApi, products } = useApi()
  const locale = useLocale()
  const currencyCode = useCurrencyCode()
  const { mapFilters } = useAgentforceFacetMappings()
  const usidCookie = useCookie('x-usid')
  const { sendSierraMessage, pollSierra, uploadSierraFiles } = useSierraChat()

  // ── Reactive state ──
  const isChatOpen = useState('agentforceOpen', () => false)
  const chatWidth = ref(420)
  const isDesktop = ref(true)
  /** Comma-joined failure codes when usid/basketId are missing; null when healthy. */
  const sessionPreconditionError = ref<string | null>(null)
  let _closeTimer: ReturnType<typeof setTimeout> | null = null
  let _pendingAuthRestart = false
  let _cachedCustomerInfo: Record<string, any> | null = null
  // Products currently rendered in the WelcomeCard recs/trending carousel.
  // Used by the `navigate` handler to attribute a PDP click back to the tile
  // the shopper tapped so the agent can be told what they are viewing.
  let _lastWelcomeRecs: WelcomeProduct[] = []
  let _pdpContextQueued = false

  // Queue (do not send) viewing context. The widget flushes this on the next
  // real user sendMessage — same pending buffer as notifyProductsShown.
  function formatPdpViewingContext(
    id: string,
    name: string,
    price?: string | number | null,
    currency?: string,
  ) {
    const hasPrice = price !== undefined && price !== null && price !== ''
    const pricePart = hasPrice ? `, ${currency || currencyCode} ${price}` : ''
    return `[%INTERNAL%] User is now viewing ${name} (${id})${pricePart}. Do not respond.`
  }

  function queuePdpViewingContext(path: string) {
    const id = getUrlId(path, 'Product')
    if (!id) {
      // The widget has one pending slot shared with products-shown context; only clear what we queued.
      if (_pdpContextQueued) setPendingContext('')
      _pdpContextQueued = false
      return
    }
    _pdpContextQueued = true

    const fromStore = agentProductsStore.products.find((p) => p.id === id)
    const fromWelcome = _lastWelcomeRecs.find((p) => p.id === id)
    const name = fromStore?.name || fromWelcome?.title || id
    const price = fromStore?.price?.lowCurrent ?? fromWelcome?.price
    const currency = fromStore?.currency || fromWelcome?.currency || currencyCode
    setPendingContext(formatPdpViewingContext(id, name, price, currency))

    // Direct PDP: no PLP/welcome row — fill name and price from catalog details.
    if (fromStore || fromWelcome) return
    void enrichPdpViewingContext(id)
  }

  async function enrichPdpViewingContext(id: string) {
    try {
      const raw = await products.$details(id)
      if (getUrlId(route.path, 'Product') !== id) return
      const name = raw?.name || id
      const price = raw?.price?.lowCurrent
      const currency = raw?.currency || currencyCode
      setPendingContext(formatPdpViewingContext(id, name, price, currency))
    }
    catch {
      // Keep the id-only pending line already queued.
    }
  }

  watch(
    [() => route.path, () => isReady?.value],
    () => { queuePdpViewingContext(route.path) },
    { immediate: true },
  )

  // ── Lifecycle ──
  const onResize = () => {
    isDesktop.value = window.innerWidth > MOBILE_BREAKPOINT
  }

  const onRouterLinkClick = (e: MouseEvent) => {
    const target = (e.target as HTMLElement).closest('[data-router-link]') as HTMLElement | null
    if (!target) return
    e.preventDefault()
    const path = target.getAttribute('data-router-link')
    if (path) router.push(path)
  }

  onMounted(() => {
    isDesktop.value = window.innerWidth > MOBILE_BREAKPOINT
    window.addEventListener('resize', onResize)
    document.addEventListener('click', onRouterLinkClick)

    if (authStore.loggedIn && !_cachedCustomerInfo) {
      buildCustomerInfoPayload()
        .then((payload) => { _cachedCustomerInfo = payload })
        .catch((err) => console.warn('[agentforce] Pre-warm customerInfo failed:', err))
    }
  })

  function amaButtonLocation() {
    const name = String(route.name || '')
    const path = String(route.path || '')
    if (name === 'p-slug' || path.includes('/p/')) return 'PDP'
    if (name === 'c-slug' || path.includes('/c/')) return 'PLP'
    if (name === 'search' || path.includes('/search')) return 'Search Modal'
    if (name === 'agent-results' || path.endsWith('/agent-results')) return 'Ask Me Anything'
    return 'Home'
  }

  // ── Chat open / close ──
  let _skipCloseAnalytics = false
  let _closeAnalyticsSent = false

  watch(isChatOpen, (open, wasOpen) => {
    if (open && !wasOpen) {
      _closeAnalyticsSent = false
    }
  })

  function onChatClose() {
    // X/backdrop emits close; Agentforce.vue then calls widget close() when
    // isChatOpen flips, which emits close again. One Close per open session.
    if (!_skipCloseAnalytics && !_closeAnalyticsSent) {
      _closeAnalyticsSent = true
      $gtm?.push?.('agentforce.onAction', 'Close', undefined, amaButtonLocation())
    }
    _closeTimer = setTimeout(() => {
      isChatOpen.value = false
    }, 300)
  }

  function dismissChatWithoutCloseEvent() {
    _skipCloseAnalytics = true
    chatClose()
    isChatOpen.value = false
    void nextTick(() => {
      _skipCloseAnalytics = false
    })
  }

  async function onChatOpen() {
    if (_closeTimer) {
      clearTimeout(_closeTimer)
      _closeTimer = null
    }
    isChatOpen.value = true

    if (_pendingAuthRestart) {
      _pendingAuthRestart = false
      await restartSession()
    }
  }

  function onGoBack() {
    window.history.back()
  }

  // ── Router guards ──
  const disposeAfterEach = router.afterEach((to, from) => {
    if (!to.path.includes('/p/')) {
      agentProductsStore.agentTriggeredPDP = false
      return
    }

    if (!from.path.endsWith('/agent-results')) return
    if (agentProductsStore.agentTriggeredPDP) {
      agentProductsStore.agentTriggeredPDP = false
      return
    }

    const product = agentProductsStore.products.find((p) => to.path.includes(p.id))
    if (!product) return

    postCtx(`[%INTERNAL%] User is now viewing ${product.name} (${product.id}). Do not respond.`)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
    document.removeEventListener('click', onRouterLinkClick)
    disposeAfterEach()
  })

  // ── Chat config ──
  const chatConfig = computed(() => ({
    apiBase: '/fapi/agentforce',
    pushContent: isDesktop.value,
    showLauncher: false,
    locale,
    labels: ($t as any).agentforce,
    sierraChat: sendSierraMessage,
    sierraPoll: pollSierra,
    sierraUpload: uploadSierraFiles,
    sessionBlocked: !!sessionPreconditionError.value,
    onSessionRetry,
    sessionMetadata: async () => {
      const failures: string[] = []

      const usidMatch = typeof document !== 'undefined'
        ? document.cookie.match(/(?:^|;\s*)x-usid=([^;]*)/)
        : null
      const usid = usidMatch?.[1] ? decodeURIComponent(usidMatch[1]) : ''
      if (!usid) {
        console.warn('[agentforce] x-usid cookie missing during sessionMetadata')
      }

      try {
        await cart.get()
      }
      catch (err: any) {
        const msg = String(err?.message ?? err).slice(0, 120)
        failures.push(`basket_fetch_failed:${msg}`)
        console.warn('[agentforce] cart.get() failed during sessionMetadata:', err)
      }

      if (!cart.basketId) {
        failures.push('missing_basketId')
      }

      sessionPreconditionError.value = failures.length ? failures.join(',') : null

      return {
        ...(cart.basketId ? { basketId: cart.basketId } : {}),
        locale: $locale,
      }
    },
  }))

  /** Same convention as `getUrlSafeFilters`: encode `;` in every filter value so the URL matches `/search`. */
  function toUrlSafeFilters(filters?: Record<string, any>): Record<string, any> | undefined {
    if (!filters) return undefined
    return Object.fromEntries(Object.entries(filters).map(([key, value]) => [
      key,
      Array.isArray(value)
        ? value.map((item) => typeof item === 'string' ? item.replace(/;/g, '%3B') : item)
        : typeof value === 'string' ? value.replace(/;/g, '%3B') : value,
    ]))
  }

  function navigateToAgentResults(q?: string, catalogFilters?: Record<string, any>) {
    const query: Record<string, string> = {}
    if (q) query.q = q
    if (q && catalogFilters && Object.keys(catalogFilters).length)
      query.filters = JSON.stringify(catalogFilters)

    router.push({ path: '/agent-results', query })
  }

  // ── View all ──
  async function onViewAll(payload: { searchResults: any[], description?: string }) {
    // The URL q must equal store.searchQuery, or agent-results re-searches and drops the agent's results.
    const q = agentProductsStore.searchQuery || payload.description || ''
    agentProductsStore.setProducts({
      searchResults: payload.searchResults,
      showMore: true,
      description: payload.description,
      total: agentProductsStore.totalProducts || payload.searchResults.length,
      searchQuery: q,
    })
    dismissChatWithoutCloseEvent()
    const mappedFilters = q ? toUrlSafeFilters(await mapFilters(agentProductsStore.filters)) : undefined
    navigateToAgentResults(q, mappedFilters || toUrlSafeFilters(agentProductsStore.filters))
  }

  // ── Search trigger ──
  let _searchInFlight = false

  async function onSearchTrigger(payload: {
    query: string
    offset?: number
    filters?: Record<string, any>
    message?: string
  }) {
    if (!payload?.query || _searchInFlight) return
    _searchInFlight = true

    try {
      // Show More often omits filters; inherit the current search only when this is
      // a continuation (offset > 0). A new search with no filters must start clean.
      const mappedFilters = payload.filters !== undefined
        ? toUrlSafeFilters(await mapFilters(payload.filters))
        : (typeof payload.offset === 'number' && payload.offset > 0
            ? agentProductsStore.filters
            : undefined)
      // The agent can send offset > 0 alongside a new query or new filters; only a
      // continuation of the results already on screen is a load-more.
      const isLoadMore = typeof payload.offset === 'number' && payload.offset > 0
        && agentProductsStore.active
        && payload.query === agentProductsStore.searchQuery
        && JSON.stringify(mappedFilters ?? null) === JSON.stringify(agentProductsStore.filters ?? null)
      const offset = isLoadMore ? (payload.offset || 0) : 0

      const raw = await products.$search({
        q: payload.query,
        start: offset,
        count: 10,
        ...(mappedFilters && { filters: getFiltersQueryString(JSON.stringify(mappedFilters)) }),
      })

      const searchResults = (raw.products || []).map((p) => ({
        productId: p.id,
        ProductName: p.name,
        pageUrl: p.url,
        Price: p.price?.lowCurrent,
        ListPrice: p.price?.lowOriginal ?? p.price?.lowCurrent,
        Currency: p.currency,
        image: p.gallery?.[0]?.src || '',
        rating: p.rating?.score || 0,
        reviewCount: p.rating?.reviewsCount || 0,
        colorCount: p.attributes?.find((a) => a.type === 'color')?.options?.length || 0,
      }))

      const total = raw.total || searchResults.length
      agentProductsStore.filters = mappedFilters
      const safeFacets = raw.filters ? getUrlSafeFilters(raw.filters) : []

      if (isLoadMore) {
        const newOffset = offset + searchResults.length
        agentProductsStore.appendProducts({
          searchResults,
          showMore: newOffset < total,
        })
      }
      else {
        agentProductsStore.setProducts({
          searchResults,
          showMore: searchResults.length < total,
          description: payload.query,
          total,
          searchQuery: payload.query,
          filterFacets: safeFacets,
          sortOptions: raw.sortOptions,
        })
        navigateToAgentResults(payload.query, mappedFilters)
      }

      renderSearchResults({
        searchResults,
        description: payload.query,
        total,
      })

      const startIndex = offset + 1
      const summary = searchResults
        .map((p: any, i: number) => `${startIndex + i}. ${p.ProductName} (${p.productId})`)
        .join(', ')
      const fyiMsg = isLoadMore
        ? `[%INTERNAL%] Frontend search results: User browsed more products for "${payload.query}". Now showing ${offset + searchResults.length} of ${total}. Products loaded: ${summary}.`
        : `[%INTERNAL%] Frontend search results: ${total} products found. Results: ${summary}.`
      postCtx(fyiMsg)
    }
    catch (err: any) {
      console.error('[agentforce] search trigger FAILED for', payload.query, err)
      setLoading(false)
      const errorMessage = ($t as any).agentforce?.unrecognizedResponse
        || 'Sorry, something went wrong. Please try again.'
      pushBotMessage(errorMessage)
      $gtm?.push?.('agentforce.onError', errorMessage)
    }
    finally {
      _searchInFlight = false
    }
  }

  // ── Checkout ──
  function onCheckout(_url: string) {
    // Cart card Checkout closes the panel to show cart; same as View All — not X / backdrop.
    dismissChatWithoutCloseEvent()
    router.push(`/${locale}/cart`)
  }

  async function onCartDetails() {
    try {
      await cart.get()
    }
    catch (err) {
      log.error(`[agentforce] cart.get() failed after cartDetails: ${err}`)
    }
  }

  // ── Product detail ──
  async function onProductDetail(payload: { pid: string, replaceCurrent?: boolean, additionalText?: string }) {
    if (!payload?.pid) return
    const replaceCurrent = !!payload.replaceCurrent

    try {
      const raw = await products.$details(payload.pid)

      const DESCRIPTION_IDS = ['productDescription', 'productDetails', 'productText', 'productFeatures', 'featuresBulletin']
      const descriptionDetail = (raw?.details || []).find((d) => DESCRIPTION_IDS.includes(d.id))
      const longDescription = descriptionDetail?.data?.[0]?.text
        || descriptionDetail?.data?.map((d) => d.text).filter(Boolean).join('\n')
        || ''
      const enrichedProduct = longDescription ? { ...raw, longDescription } : raw

      if (replaceCurrent) {
        updateLastProductDetails(enrichedProduct)
      }
      else {
        renderProductDetails(enrichedProduct, payload.additionalText)
      }

      if (replaceCurrent) return
      const fallbackMatch = agentProductsStore.products.find((p) => p.id === payload.pid)
      const url = raw?.url || fallbackMatch?.url
      if (url) {
        agentProductsStore.agentTriggeredPDP = true
        $gtm?.push?.('agentforce.onAction', 'Go To PDP', payload.pid, amaButtonLocation())
        router.push(url)
      }
      else { console.warn('[agentforce] no PDP url available for', payload.pid) }
    }
    catch (err: any) {
      console.error('[agentforce] product details fetch FAILED for', payload.pid, {
        status: err?.statusCode || err?.status,
        message: err?.message,
        data: err?.data,
        responseData: err?.response?._data,
      })
      if (!replaceCurrent) renderProductDetails(null)
    }
  }

  // ── Customer info ──
  async function buildCustomerInfoPayload(): Promise<Record<string, any>> {
    const profile = userStore.profile
    const details = userStore.details

    const customerBase = {
      birthDate: details?.birthDate,
      consumerId: details?.consumerId,
      firstName: details?.firstName,
      isLoyaltyMember: profile?.isLoyaltyMember,
      loyaltySummary: profile?.loyaltySummary,
    }

    const ordersPromise = consumerApi.$getOrderHistory({
      pageNumber: 1,
      pageSize: 10,
      paginationStrategy: 'NEXTPAGE',
      searchPeriod: '6',
    })

    const detailPromise = ordersPromise.then((r) =>
      r?.orders?.[0]?.order_id
        ? consumerApi.$getOrderDetails(r.orders[0].order_id).then((d) => d?.[0] ?? null)
        : Promise.resolve(null),
    )

    const [ordersResponse, detailResponse] = await Promise.allSettled([ordersPromise, detailPromise])

    const ordersData = ordersResponse.status === 'fulfilled' ? ordersResponse.value : null
    if (ordersResponse.status === 'rejected') {
      console.warn('[agentforce] Orders fetch failed:', ordersResponse.reason)
    }

    if (!ordersData?.orders?.length) {
      return { customer: customerBase, orders: [], latestOrder: null }
    }

    const ordersSummary = ordersData.orders.map((o: any) => ({
      OrderNumber: o.order_number,
      OrderStatus: o.status,
      OrderDate: o.order_date,
      ItemCount: o.order_lines?.length ?? 0,
    }))

    const firstOrder = ordersData.orders[0]
    const detail = detailResponse.status === 'fulfilled' ? detailResponse.value : null
    if (detailResponse.status === 'rejected') {
      console.warn('[agentforce] Order detail fetch failed:', detailResponse.reason)
    }

    const firstShipment = detail?.shipments?.flat()?.[0]
    const shippingService = firstShipment?.shippingMethod?.services?.[0]
    const shippingStatusLabel = firstShipment?.shippingStatusLabel

    return {
      customer: customerBase,
      orders: ordersSummary,
      latestOrder: {
        orderNumber: firstOrder.order_number,
        status: shippingStatusLabel ?? firstOrder.status,
        orderId: firstOrder.order_id,
        orderDate: firstOrder.order_date,
        orderDetailUrl: `/${locale}/account/order-details/${firstOrder.order_id}`,
        estimatedDelivery: firstShipment?.extnEstimatedDeliveryDate || shippingService?.shippingDuration,
        orderItems: (firstOrder.order_lines || []).map((line: any) => ({
          name: line.name,
          imageUrl: line.img || undefined,
        })),
      },
    }
  }

  // ── BloomReach products ──
  function extractBrProducts(): WelcomeProduct[] {
    const rawBrProducts: any[] = (window as any).vfbr?.[0] || []
    return rawBrProducts.slice(0, 4).map((p: any) => {
      try {
        const extracted = extractBloomReachProduct(p, false)
        return {
          id: extracted.id,
          title: extracted.title,
          image: extracted.image?.src ?? '',
          price: extracted.price?.lowCurrent ?? '',
          currency: p.currency || currencyCode,
          url: extracted.url,
        }
      }
      catch {
        return null
      }
    }).filter(Boolean) as WelcomeProduct[]
  }

  async function localizeProducts(welcomeProducts: WelcomeProduct[]) {
    if (!welcomeProducts.length) return welcomeProducts
    if (locale.startsWith('en')) return welcomeProducts

    try {
      const ids = welcomeProducts.map((p) => p.id)
      const raw = await products.$search({
        q: ids.join(' OR '),
        count: ids.length,
      })

      const apiMap = new Map<string, any>()
      for (const p of raw.products || []) {
        apiMap.set(p.id, p)
      }

      return welcomeProducts.map((p) => {
        const localized = apiMap.get(p.id)
        if (!localized) return p
        return {
          ...p,
          title: localized.name || p.title,
          price: localized.price?.lowCurrent ?? p.price,
          currency: localized.currency || p.currency,
          url: localized.url || p.url,
          image: localized.gallery?.[0]?.src || p.image,
        }
      })
    }
    catch (err) {
      console.warn('[agentforce] Failed to localize products, using BloomReach data:', err)
      return welcomeProducts
    }
  }

  // ── Auth transition watch ──
  watch(() => authStore.loggedIn, async (newVal, oldVal) => {
    _cachedCustomerInfo = null
    if (oldVal === undefined) return
    if (!isChatOpen.value) {
      _pendingAuthRestart = true
      return
    }

    if (!usidCookie.value) {
      await new Promise<void>((resolve) => {
        let attempts = 0
        const check = setInterval(() => {
          attempts++
          if (usidCookie.value || attempts >= 20) {
            clearInterval(check)
            resolve()
          }
        }, 150)
      })
    }

    if (newVal && !userStore.details) {
      await userStore.get()
    }

    await restartSession()
  })

  // ── Welcome ──
  async function onWelcome() {
    const brProducts = extractBrProducts()

    if (!authStore.loggedIn) {
      const trendingProducts = await localizeProducts(brProducts)
      _lastWelcomeRecs = trendingProducts
      setWelcomeContext({
        customer: { firstName: '' },
        orders: [],
        ...(trendingProducts.length ? { trendingProducts } : {}),
      })
      return
    }

    try {
      const [payload, localizedProducts] = await Promise.all([
        _cachedCustomerInfo ?? buildCustomerInfoPayload(),
        localizeProducts(brProducts),
      ])

      const welcomePayload = {
        ...payload,
        ...(localizedProducts.length
          ? { recommendedProducts: localizedProducts }
          : {}),
      }

      _lastWelcomeRecs = localizedProducts
      setWelcomeContext(welcomePayload)
    }
    catch (err) {
      console.error('[agentforce] Failed to fetch welcome context:', err)
      setWelcomeContext({ customer: {}, orders: [], latestOrder: null })
    }
  }

  // ── Return label ──
  async function onReturnLabel(payload: { orderNumber: string }) {
    try {
      const { returnLabel } = await consumerApi.$returnLabel(
        payload.orderNumber,
        { body: 'null' },
      )
      const byteCharacters = atob(returnLabel)
      const byteNumbers = new Uint8Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++)
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      const blob = new Blob([byteNumbers], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      sendSilentMessage(
        `[%INTERNAL%] Return label generated for order ${payload.orderNumber}.`,
        { suppressResponse: true },
      )

      const agentLabels = ($t as any).agentforce || {}
      const defaultLabel = 'Here\'s your return label for order {orderNumber}.'
        + ' <a href="{url}" download="return-label-{orderNumber}.pdf"'
        + ' target="_blank">Click here to download</a>.'
      const linkHtml = (agentLabels.returnLabelSuccess || defaultLabel)
        .replace(/\{orderNumber\}/g, payload.orderNumber)
        .replace(/\{url\}/g, url)
      pushBotMessage(linkHtml)
    }
    catch (err) {
      console.error('[agentforce] Return label fetch failed:', err)
      const agentLabelsErr = ($t as any).agentforce || {}
      const defaultErr = 'Sorry, we couldn\'t retrieve the return label'
        + ' for order {orderNumber}. Please try again later.'
      pushBotMessage(
        (agentLabelsErr.returnLabelError || defaultErr)
          .replace(/\{orderNumber\}/g, payload.orderNumber),
      )
    }
  }

  // Shopper tapped a link inside the chat (WelcomeCard recs, order details,
  // etc.). Mirrors `onSearchTrigger` / router.afterEach: if the destination is
  // a PDP for a WelcomeCard rec, tell the agent what they are viewing so it
  // has the same context it gets from search results. On mobile the chat
  // window overlays the page — close it so the shopper can see the PDP.
  function onNavigate(path: string) {
    if (path.includes('/p/')) {
      const rec = _lastWelcomeRecs.find((p) => p.id && path.includes(p.id))
      if (rec) {
        postCtx(`[%INTERNAL%] User is now viewing ${rec.title} (${rec.id}). Do not respond.`)
      }
      const pid = rec?.id || getUrlId(path, 'Product')
      if (pid) $gtm?.push?.('agentforce.onAction', 'Go To PDP', pid, amaButtonLocation())
    }
    router.push(path)
    if (!isDesktop.value) dismissChatWithoutCloseEvent()
  }

  async function onSessionRetry() {
    sessionPreconditionError.value = null
    await restartSession()
  }

  function onQuestionClick(text: string) {
    $gtm?.push?.('agentforce.onAction', 'Question Click', text, amaButtonLocation())
  }

  function onAmaError(message: string) {
    $gtm?.push?.('agentforce.onError', message)
  }

  const handlers = {
    cartDetails: onCartDetails,
    checkout: onCheckout,
    close: onChatClose,
    goBack: onGoBack,
    navigate: onNavigate,
    open: onChatOpen,
    productDetail: onProductDetail,
    questionClick: onQuestionClick,
    amaError: onAmaError,
    resize: (w: number) => { chatWidth.value = w },
    returnLabel: onReturnLabel,
    searchTrigger: onSearchTrigger,
    viewAll: onViewAll,
    welcome: onWelcome,
  }

  return {
    chatConfig,
    chatWidth,
    handlers,
    isChatOpen,
    isDesktop,
    sessionPreconditionError,
    userStore,
  }
}
